import React, { useState, useEffect } from 'react';
import { FaPlay, FaLock, FaCheck, FaVolumeUp, FaGraduationCap } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import './AlphabetGrid.css';

interface AlphabetLetter {
  id: string;
  order: number;
  letter: string;
  letterName: string;
  letterNameEnglish: string;
  letterNamePersian: string;
  pronunciation: string;
  description: string;
  examples: string[];
  exampleAudioUrls: string[]; // Add audio URLs for each example
  audioUrl?: string;
  isCompleted?: boolean;
  isLocked?: boolean;
  lessonId: string; // Add lessonId field
}

const AlphabetGrid: React.FC = () => {
  const [completedLetters, setCompletedLetters] = useState<string[]>([]);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [audioErrors, setAudioErrors] = useState<Record<string, string>>({});

  // Function to map letter IDs to lesson IDs
  const getLessonId = (order: number): string => {
    // Map to the actual lesson IDs used in the GridAlphabetLesson component
    const letterToIdMap: { [key: number]: string } = {
      1: 'alphabet-1-alef',
      2: 'alphabet-2-be',
      3: 'alphabet-3-pe',
      4: 'alphabet-4-te',
      5: 'alphabet-5-se',
      6: 'alphabet-6-jim',
      7: 'alphabet-7-che',
      8: 'alphabet-8-he',
      9: 'alphabet-9-khe',
      10: 'alphabet-10-dal',
      11: 'alphabet-11-zal',
      12: 'alphabet-12-re',
      13: 'alphabet-13-ze',
      14: 'alphabet-14-zhe',
      15: 'alphabet-15-sin',
      16: 'alphabet-16-shin',
      17: 'alphabet-17-sad',
      18: 'alphabet-18-zad',
      19: 'alphabet-19-ta',
      20: 'alphabet-20-za',
      21: 'alphabet-21-ayn',
      22: 'alphabet-22-ghayn',
      23: 'alphabet-23-fe',
      24: 'alphabet-24-qaf',
      25: 'alphabet-25-kaf',
      26: 'alphabet-26-gaf',
      27: 'alphabet-27-lam',
      28: 'alphabet-28-mim',
      29: 'alphabet-29-nun',
      30: 'alphabet-30-vav',
      31: 'alphabet-31-he',
      32: 'alphabet-32-ye'
    };
    return letterToIdMap[order] || `alphabet-${order}-unknown`;
  };

  // Function to generate organized audio URL
  const generateOrganizedAudioUrl = (text: string): string => {
    // Simple hash function for client-side use
    const hash = text.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0).toString(16);
    return API_BASE_URL + '/audio/organized/audio-file.mp3';
  };

  // Complete Persian alphabet data - 32 letters with progressive learning
  const alphabetLetters: AlphabetLetter[] = [
    {
      id: 'آ',
      order: 1,
      letter: 'آ',
      letterName: 'Alef',
      letterNameEnglish: 'Alef',
      letterNamePersian: 'الف',
      pronunciation: 'alef',
      description: 'The first letter of the Persian alphabet. Represents the sound "a" and is one of the most common letters.',
      examples: ['آب', 'آسمان', 'آفتاب'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('آب'),
        generateOrganizedAudioUrl('آسمان'),
        generateOrganizedAudioUrl('آفتاب')
      ],
      audioUrl: generateOrganizedAudioUrl('آ'),
      lessonId: getLessonId(1)
    },
    {
      id: 'ب',
      order: 2,
      letter: 'ب',
      letterName: 'Be',
      letterNameEnglish: 'Be',
      letterNamePersian: 'ب',
      pronunciation: 'be',
      description: 'The second letter representing the sound "b". Used in many common Persian words.',
      examples: ['بابا', 'برادر', 'باغ'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('بابا'),
        generateOrganizedAudioUrl('برادر'),
        generateOrganizedAudioUrl('باغ')
      ],
      audioUrl: generateOrganizedAudioUrl('ب'),
      lessonId: getLessonId(2)
    },
    {
      id: 'پ',
      order: 3,
      letter: 'پ',
      letterName: 'Pe',
      letterNameEnglish: 'Pe',
      letterNamePersian: 'پ',
      pronunciation: 'pe',
      description: 'The third letter representing the sound "p". Unique to Persian (not found in Arabic).',
      examples: ['پدر', 'پسر', 'پنجره'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('پدر'),
        generateOrganizedAudioUrl('پسر'),
        generateOrganizedAudioUrl('پنجره')
      ],
      audioUrl: generateOrganizedAudioUrl('پ'),
      lessonId: getLessonId(3)
    },
    {
      id: 'ت',
      order: 4,
      letter: 'ت',
      letterName: 'Te',
      letterNameEnglish: 'Te',
      letterNamePersian: 'ت',
      pronunciation: 'te',
      description: 'The fourth letter representing the sound "t". Very common in Persian words.',
      examples: ['تو', 'تخت', 'تاب'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('تو'),
        generateOrganizedAudioUrl('تخت'),
        generateOrganizedAudioUrl('تاب')
      ],
      audioUrl: generateOrganizedAudioUrl('ت'),
      lessonId: getLessonId(4)
    },
    {
      id: 'ث',
      order: 5,
      letter: 'ث',
      letterName: 'Se',
      letterNameEnglish: 'Se',
      letterNamePersian: 'ث',
      pronunciation: 'se',
      description: 'The fifth letter representing the "th" sound. Less common in Persian.',
      examples: ['ثالث', 'ثروت', 'ثواب'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('ثالث'),
        generateOrganizedAudioUrl('ثروت'),
        generateOrganizedAudioUrl('ثواب')
      ],
      audioUrl: generateOrganizedAudioUrl('ث'),
      lessonId: getLessonId(5)
    },
    {
      id: 'ج',
      order: 6,
      letter: 'ج',
      letterName: 'Jim',
      letterNameEnglish: 'Jim',
      letterNamePersian: 'ج',
      pronunciation: 'jim',
      description: 'The sixth letter representing the sound "j". Used in many Persian words.',
      examples: ['جوان', 'جنگل', 'جام'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('جوان'),
        generateOrganizedAudioUrl('جنگل'),
        generateOrganizedAudioUrl('جام')
      ],
      audioUrl: generateOrganizedAudioUrl('ج'),
      lessonId: getLessonId(6)
    },
    {
      id: 'چ',
      order: 7,
      letter: 'چ',
      letterName: 'Che',
      letterNameEnglish: 'Che',
      letterNamePersian: 'چ',
      pronunciation: 'che',
      description: 'The seventh letter representing the sound "ch". Unique to Persian.',
      examples: ['چای', 'چشم', 'چوب'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('چای'),
        generateOrganizedAudioUrl('چشم'),
        generateOrganizedAudioUrl('چوب')
      ],
      audioUrl: generateOrganizedAudioUrl('چ'),
      lessonId: getLessonId(7)
    },
    {
      id: 'ح',
      order: 8,
      letter: 'ح',
      letterName: 'He',
      letterNameEnglish: 'He',
      letterNamePersian: 'ح',
      pronunciation: 'he',
      description: 'The eighth letter representing a guttural "h" sound.',
      examples: ['حال', 'خانه', 'حرف'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('حال'),
        generateOrganizedAudioUrl('خانه'),
        generateOrganizedAudioUrl('حرف')
      ],
      audioUrl: generateOrganizedAudioUrl('ح'),
      lessonId: getLessonId(8)
    },
    {
      id: 'خ',
      order: 9,
      letter: 'خ',
      letterName: 'Khe',
      letterNameEnglish: 'Khe',
      letterNamePersian: 'خ',
      pronunciation: 'khe',
      description: 'The ninth letter representing the "kh" sound, like in Scottish "loch".',
      examples: ['خوب', 'خورشید', 'خواب'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('خوب'),
        generateOrganizedAudioUrl('خورشید'),
        generateOrganizedAudioUrl('خواب')
      ],
      audioUrl: generateOrganizedAudioUrl('خ'),
      lessonId: getLessonId(9)
    },
    {
      id: 'د',
      order: 10,
      letter: 'د',
      letterName: 'Dal',
      letterNameEnglish: 'Dal',
      letterNamePersian: 'د',
      pronunciation: 'dal',
      description: 'The tenth letter representing the sound "d". Very common in Persian.',
      examples: ['دست', 'درخت', 'دوست'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('دست'),
        generateOrganizedAudioUrl('درخت'),
        generateOrganizedAudioUrl('دوست')
      ],
      audioUrl: generateOrganizedAudioUrl('د'),
      lessonId: getLessonId(10)
    },
    {
      id: 'ذ',
      order: 11,
      letter: 'ذ',
      letterName: 'Zal',
      letterNameEnglish: 'Zal',
      letterNamePersian: 'ذ',
      pronunciation: 'zal',
      description: 'The eleventh letter representing the "z" sound.',
      examples: ['ذکر', 'ذره', 'ذوق'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('ذکر'),
        generateOrganizedAudioUrl('ذره'),
        generateOrganizedAudioUrl('ذوق')
      ],
      audioUrl: generateOrganizedAudioUrl('ذ'),
      lessonId: getLessonId(11)
    },
    {
      id: 'ر',
      order: 12,
      letter: 'ر',
      letterName: 'Re',
      letterNameEnglish: 'Re',
      letterNamePersian: 'ر',
      pronunciation: 're',
      description: 'The twelfth letter representing the sound "r". Very common in Persian.',
      examples: ['روز', 'رنگ', 'راه'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('روز'),
        generateOrganizedAudioUrl('رنگ'),
        generateOrganizedAudioUrl('راه')
      ],
      audioUrl: generateOrganizedAudioUrl('ر'),
      lessonId: getLessonId(12)
    },
    {
      id: 'ز',
      order: 13,
      letter: 'ز',
      letterName: 'Ze',
      letterNameEnglish: 'Ze',
      letterNamePersian: 'ز',
      pronunciation: 'ze',
      description: 'The thirteenth letter representing the sound "z". Common in Persian.',
      examples: ['زن', 'زبان', 'زمین'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('زن'),
        generateOrganizedAudioUrl('زبان'),
        generateOrganizedAudioUrl('زمین')
      ],
      audioUrl: generateOrganizedAudioUrl('ز'),
      lessonId: getLessonId(13)
    },
    {
      id: 'ژ',
      order: 14,
      letter: 'ژ',
      letterName: 'Zhe',
      letterNameEnglish: 'Zhe',
      letterNamePersian: 'ژ',
      pronunciation: 'zhe',
      description: 'The fourteenth letter representing the "zh" sound, like in "vision".',
      examples: ['ژاله', 'ژنرال', 'ژاپن'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('ژاله'),
        generateOrganizedAudioUrl('ژنرال'),
        generateOrganizedAudioUrl('ژاپن')
      ],
      audioUrl: generateOrganizedAudioUrl('ژ'),
      lessonId: getLessonId(14)
    },
    {
      id: 'س',
      order: 15,
      letter: 'س',
      letterName: 'Sin',
      letterNameEnglish: 'Sin',
      letterNamePersian: 'س',
      pronunciation: 'sin',
      description: 'The fifteenth letter representing the sound "s". Very common in Persian.',
      examples: ['سلام', 'ساعت', 'سفید'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('سلام'),
        generateOrganizedAudioUrl('ساعت'),
        generateOrganizedAudioUrl('سفید')
      ],
      audioUrl: generateOrganizedAudioUrl('س'),
      lessonId: getLessonId(15)
    },
    {
      id: 'ش',
      order: 16,
      letter: 'ش',
      letterName: 'Shin',
      letterNameEnglish: 'Shin',
      letterNamePersian: 'ش',
      pronunciation: 'shin',
      description: 'The sixteenth letter representing the "sh" sound.',
      examples: ['شمع', 'شیر', 'شهر'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('شمع'),
        generateOrganizedAudioUrl('شیر'),
        generateOrganizedAudioUrl('شهر')
      ],
      audioUrl: generateOrganizedAudioUrl('ش'),
      lessonId: getLessonId(16)
    },
    {
      id: 'ص',
      order: 17,
      letter: 'ص',
      letterName: 'Sad',
      letterNameEnglish: 'Sad',
      letterNamePersian: 'ص',
      pronunciation: 'sad',
      description: 'The seventeenth letter representing the "s" sound (emphatic).',
      examples: ['صبح', 'صندلی', 'صدا'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('صبح'),
        generateOrganizedAudioUrl('صندلی'),
        generateOrganizedAudioUrl('صدا')
      ],
      audioUrl: generateOrganizedAudioUrl('ص'),
      lessonId: getLessonId(17)
    },
    {
      id: 'ض',
      order: 18,
      letter: 'ض',
      letterName: 'Zad',
      letterNameEnglish: 'Zad',
      letterNamePersian: 'ض',
      pronunciation: 'zad',
      description: 'The eighteenth letter representing the "z" sound (emphatic).',
      examples: ['ضرب', 'ضیافت', 'ضمانت'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('ضرب'),
        generateOrganizedAudioUrl('ضیافت'),
        generateOrganizedAudioUrl('ضمانت')
      ],
      audioUrl: generateOrganizedAudioUrl('ض'),
      lessonId: getLessonId(18)
    },
    {
      id: 'ط',
      order: 19,
      letter: 'ط',
      letterName: 'Ta',
      letterNameEnglish: 'Ta',
      letterNamePersian: 'ط',
      pronunciation: 'ta',
      description: 'The nineteenth letter representing the "t" sound (emphatic).',
      examples: ['طبیعت', 'طلا', 'طعم'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('طبیعت'),
        generateOrganizedAudioUrl('طلا'),
        generateOrganizedAudioUrl('طعم')
      ],
      audioUrl: generateOrganizedAudioUrl('ط'),
      lessonId: getLessonId(19)
    },
    {
      id: 'ظ',
      order: 20,
      letter: 'ظ',
      letterName: 'Za',
      letterNameEnglish: 'Za',
      letterNamePersian: 'ظ',
      pronunciation: 'za',
      description: 'The twentieth letter representing the "z" sound (emphatic).',
      examples: ['ظهر', 'ظاهر', 'ظلم'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('ظهر'),
        generateOrganizedAudioUrl('ظاهر'),
        generateOrganizedAudioUrl('ظلم')
      ],
      audioUrl: generateOrganizedAudioUrl('ظ'),
      lessonId: getLessonId(20)
    },
    {
      id: 'ع',
      order: 21,
      letter: 'ع',
      letterName: 'Ayn',
      letterNameEnglish: 'Ayn',
      letterNamePersian: 'ع',
      pronunciation: 'ayn',
      description: 'The twenty-first letter representing a guttural sound.',
      examples: ['علم', 'عشق', 'عقل'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('علم'),
        generateOrganizedAudioUrl('عشق'),
        generateOrganizedAudioUrl('عقل')
      ],
      audioUrl: generateOrganizedAudioUrl('ع'),
      lessonId: getLessonId(21)
    },
    {
      id: 'غ',
      order: 22,
      letter: 'غ',
      letterName: 'Ghayn',
      letterNameEnglish: 'Ghayn',
      letterNamePersian: 'غ',
      pronunciation: 'ghayn',
      description: 'The twenty-second letter representing the "gh" sound.',
      examples: ['غم', 'غذا', 'غروب'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('غم'),
        generateOrganizedAudioUrl('غذا'),
        generateOrganizedAudioUrl('غروب')
      ],
      audioUrl: generateOrganizedAudioUrl('غ'),
      lessonId: getLessonId(22)
    },
    {
      id: 'ف',
      order: 23,
      letter: 'ف',
      letterName: 'Fe',
      letterNameEnglish: 'Fe',
      letterNamePersian: 'ف',
      pronunciation: 'fe',
      description: 'The twenty-third letter representing the sound "f".',
      examples: ['فردا', 'فصل', 'فکر'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('فردا'),
        generateOrganizedAudioUrl('فصل'),
        generateOrganizedAudioUrl('فکر')
      ],
      audioUrl: generateOrganizedAudioUrl('ف'),
      lessonId: getLessonId(23)
    },
    {
      id: 'ق',
      order: 24,
      letter: 'ق',
      letterName: 'Qaf',
      letterNameEnglish: 'Qaf',
      letterNamePersian: 'ق',
      pronunciation: 'qaf',
      description: 'The twenty-fourth letter representing the "q" sound.',
      examples: ['قلم', 'قدرت', 'قانون'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('قلم'),
        generateOrganizedAudioUrl('قدرت'),
        generateOrganizedAudioUrl('قانون')
      ],
      audioUrl: generateOrganizedAudioUrl('ق'),
      lessonId: getLessonId(24)
    },
    {
      id: 'ک',
      order: 25,
      letter: 'ک',
      letterName: 'Kaf',
      letterNameEnglish: 'Kaf',
      letterNamePersian: 'ک',
      pronunciation: 'kaf',
      description: 'The twenty-fifth letter representing the sound "k".',
      examples: ['کتاب', 'کودک', 'کشور'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('کتاب'),
        generateOrganizedAudioUrl('کودک'),
        generateOrganizedAudioUrl('کشور')
      ],
      audioUrl: generateOrganizedAudioUrl('ک'),
      lessonId: getLessonId(25)
    },
    {
      id: 'گ',
      order: 26,
      letter: 'گ',
      letterName: 'Gaf',
      letterNameEnglish: 'Gaf',
      letterNamePersian: 'گ',
      pronunciation: 'gaf',
      description: 'The twenty-sixth letter representing the sound "g". Unique to Persian.',
      examples: ['گل', 'گوش', 'گفتگو'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('گل'),
        generateOrganizedAudioUrl('گوش'),
        generateOrganizedAudioUrl('گفتگو')
      ],
      audioUrl: generateOrganizedAudioUrl('گ'),
      lessonId: getLessonId(26)
    },
    {
      id: 'ل',
      order: 27,
      letter: 'ل',
      letterName: 'Lam',
      letterNameEnglish: 'Lam',
      letterNamePersian: 'ل',
      pronunciation: 'lam',
      description: 'The twenty-seventh letter representing the sound "l".',
      examples: ['لب', 'لطف', 'لذت'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('لب'),
        generateOrganizedAudioUrl('لطف'),
        generateOrganizedAudioUrl('لذت')
      ],
      audioUrl: generateOrganizedAudioUrl('ل'),
      lessonId: getLessonId(27)
    },
    {
      id: 'م',
      order: 28,
      letter: 'م',
      letterName: 'Meem',
      letterNameEnglish: 'Meem',
      letterNamePersian: 'م',
      pronunciation: 'meem',
      description: 'The twenty-eighth letter representing the sound "m".',
      examples: ['مامان', 'ماه', 'مدرسه'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('مامان'),
        generateOrganizedAudioUrl('ماه'),
        generateOrganizedAudioUrl('مدرسه')
      ],
      audioUrl: generateOrganizedAudioUrl('م'),
      lessonId: getLessonId(28)
    },
    {
      id: 'ن',
      order: 29,
      letter: 'ن',
      letterName: 'Noon',
      letterNameEnglish: 'Noon',
      letterNamePersian: 'ن',
      pronunciation: 'noon',
      description: 'The twenty-ninth letter representing the sound "n".',
      examples: ['نام', 'نور', 'نگاه'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('نام'),
        generateOrganizedAudioUrl('نور'),
        generateOrganizedAudioUrl('نگاه')
      ],
      audioUrl: generateOrganizedAudioUrl('ن'),
      lessonId: getLessonId(29)
    },
    {
      id: 'و',
      order: 30,
      letter: 'و',
      letterName: 'Vav',
      letterNameEnglish: 'Vav',
      letterNamePersian: 'و',
      pronunciation: 'vav',
      description: 'The thirtieth letter representing the sound "v" or "u".',
      examples: ['وطن', 'وزن', 'وقت'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('وطن'),
        generateOrganizedAudioUrl('وزن'),
        generateOrganizedAudioUrl('وقت')
      ],
      audioUrl: generateOrganizedAudioUrl('و'),
      lessonId: getLessonId(30)
    },
    {
      id: 'ه',
      order: 31,
      letter: 'ه',
      letterName: 'He',
      letterNameEnglish: 'He',
      letterNamePersian: 'ه',
      pronunciation: 'he',
      description: 'The thirty-first letter representing the sound "h".',
      examples: ['هوا', 'خانه', 'هیچ'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('هوا'),
        generateOrganizedAudioUrl('خانه'),
        generateOrganizedAudioUrl('هیچ')
      ],
      audioUrl: generateOrganizedAudioUrl('ه'),
      lessonId: getLessonId(31)
    },
    {
      id: 'ی',
      order: 32,
      letter: 'ی',
      letterName: 'Ye',
      letterNameEnglish: 'Ye',
      letterNamePersian: 'ی',
      pronunciation: 'ye',
      description: 'The thirty-second letter representing the sound "y" or "i".',
      examples: ['یک', 'یاد', 'یاور'],
      exampleAudioUrls: [
        generateOrganizedAudioUrl('یک'),
        generateOrganizedAudioUrl('یاد'),
        generateOrganizedAudioUrl('یاور')
      ],
      audioUrl: generateOrganizedAudioUrl('ی'),
      lessonId: getLessonId(32)
    }
  ];

  // Load completed letters from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('completedAlphabetLessons');
    if (saved) {
      const parsed = JSON.parse(saved);
      
      // Migration: Convert old lesson IDs to new format
      const oldToNewIdMap: { [key: string]: string } = {
        'basic-2-1': 'alphabet-1-alef',
        'basic-2-2': 'alphabet-2-be',
        'basic-2-3': 'alphabet-3-pe',
        'basic-2-4': 'alphabet-4-te',
        'basic-2-5': 'alphabet-5-se',
        'basic-2-6': 'alphabet-6-jim',
        'basic-2-7': 'alphabet-7-che',
        'basic-2-8': 'alphabet-8-he',
        'basic-2-9': 'alphabet-9-khe',
        'basic-2-10': 'alphabet-10-dal',
        'basic-2-11': 'alphabet-11-zal',
        'basic-2-12': 'alphabet-12-re',
        'basic-2-13': 'alphabet-13-ze',
        'basic-2-14': 'alphabet-14-zhe',
        'basic-2-15': 'alphabet-15-sin',
        'basic-2-16': 'alphabet-16-shin',
        'basic-2-17': 'alphabet-17-sad',
        'basic-2-18': 'alphabet-18-zad',
        'basic-2-19': 'alphabet-19-ta',
        'basic-2-20': 'alphabet-20-za',
        'basic-2-21': 'alphabet-21-ayn',
        'basic-2-22': 'alphabet-22-ghayn',
        'basic-2-23': 'alphabet-23-fe',
        'basic-2-24': 'alphabet-24-qaf',
        'basic-2-25': 'alphabet-25-kaf',
        'basic-2-26': 'alphabet-26-gaf',
        'basic-2-27': 'alphabet-27-lam',
        'basic-2-28': 'alphabet-28-mim',
        'basic-2-29': 'alphabet-29-nun',
        'basic-2-30': 'alphabet-30-vav',
        'basic-2-31': 'alphabet-31-he',
        'basic-2-32': 'alphabet-32-ye'
      };
      
      // Migrate old IDs to new format
      const migrated = parsed.map((id: string) => oldToNewIdMap[id] || id);
      
      // Remove duplicates and save back to localStorage
      const uniqueIds = Array.from(new Set(migrated)) as string[];
      localStorage.setItem('completedAlphabetLessons', JSON.stringify(uniqueIds));
      
      setCompletedLetters(uniqueIds);
      console.log(' Migrated lesson IDs:', { original: parsed, migrated: uniqueIds });
    }
  }, []);

  const handleAudioPlay = async (letter: AlphabetLetter) => {
    if (!letter.audioUrl) return;
    
    setPlayingAudio(letter.id);
    
    try {
      console.log('Playing organized audio for letter: ' + letter.letter);
      
      // Create audio element
      const audio = new Audio(letter.audioUrl);
      
      // Add error handling
      audio.onerror = async (error) => {
        console.error('Organized audio error for letter ' + letter.letter + ':', error);
        setAudioErrors(prev => ({
          ...prev,
          [letter.id]: 'Audio generation failed - using TTS fallback'
        }));
        
        // Try TTS fallback
        try {
          console.log('Trying TTS fallback for letter: ' + letter.letter);
          if ('speechSynthesis' in window) {
            const utterance = new SpeechSynthesisUtterance(letter.letter);
            utterance.lang = 'fa-IR';
            utterance.rate = 0.6;
            speechSynthesis.speak(utterance);
            console.log('TTS fallback successful for letter: ' + letter.letter);
          }
        } catch (ttsError) {
          console.error('TTS fallback also failed for letter ' + letter.letter + ':', ttsError);
        }
        
        setPlayingAudio(null);
      };
      
      // Add success handling
      audio.onended = () => {
        console.log('Organized audio playback completed for letter: ' + letter.letter);
        setPlayingAudio(null);
      };
      
      // Play the audio
      await audio.play();
      
    } catch (error) {
              console.error('Failed to play organized audio for letter ' + letter.letter + ':', error);
      setAudioErrors(prev => ({
        ...prev,
        [letter.id]: 'Audio generation failed - using TTS fallback'
      }));
      
      // Try TTS fallback
      try {
        console.log('Trying TTS fallback for letter: ' + letter.letter);
        if ('speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance(letter.letter);
          utterance.lang = 'fa-IR';
          utterance.rate = 0.6;
          speechSynthesis.speak(utterance);
          console.log('TTS fallback successful for letter: ' + letter.letter);
        }
      } catch (ttsError) {
                  console.error('TTS fallback also failed for letter ' + letter.letter + ':', ttsError);
      }
      
      setPlayingAudio(null);
    }
  };

  const handleExampleAudioPlay = async (letter: AlphabetLetter, exampleIndex: number) => {
    const audioUrl = letter.exampleAudioUrls[exampleIndex];
    if (!audioUrl) return;
    
    const playingId = letter.id + '-example-' + exampleIndex;
    setPlayingAudio(playingId);
    
    try {
              console.log('Playing organized audio for example: ' + letter.examples[exampleIndex]);
      
      // Create audio element
      const audio = new Audio(audioUrl);
      
      // Add error handling
      audio.onerror = async (error) => {
        console.error('Organized audio error for example ' + letter.examples[exampleIndex] + ':', error);
        setAudioErrors(prev => ({
          ...prev,
          [playingId]: 'Audio generation failed - using TTS fallback'
        }));
        
        // Try TTS fallback
        try {
          console.log('Trying TTS fallback for example: ' + letter.examples[exampleIndex]);
          if ('speechSynthesis' in window) {
            const utterance = new SpeechSynthesisUtterance(letter.examples[exampleIndex]);
            utterance.lang = 'fa-IR';
            utterance.rate = 0.6;
            speechSynthesis.speak(utterance);
            console.log('TTS fallback successful for example: ' + letter.examples[exampleIndex]);
          }
        } catch (ttsError) {
          console.error('TTS fallback also failed for example ' + letter.examples[exampleIndex] + ':', ttsError);
        }
        
        setPlayingAudio(null);
      };
      
      // Add success handling
      audio.onended = () => {
        console.log('Organized audio playback completed for example: ' + letter.examples[exampleIndex]);
        setPlayingAudio(null);
      };
      
      // Play the audio
      await audio.play();
      
    } catch (error) {
              console.error('Failed to play organized audio for example ' + letter.examples[exampleIndex] + ':', error);
      setAudioErrors(prev => ({
        ...prev,
        [playingId]: 'Audio generation failed - using TTS fallback'
      }));
      
      // Try TTS fallback
      try {
        console.log('Trying TTS fallback for example: ' + letter.examples[exampleIndex]);
        if ('speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance(letter.examples[exampleIndex]);
          utterance.lang = 'fa-IR';
          utterance.rate = 0.6;
          speechSynthesis.speak(utterance);
          console.log('TTS fallback successful for example: ' + letter.examples[exampleIndex]);
        }
      } catch (ttsError) {
                  console.error('TTS fallback also failed for example ' + letter.examples[exampleIndex] + ':', ttsError);
      }
      
      setPlayingAudio(null);
    }
  };

  const isLetterAccessible = (letter: AlphabetLetter): boolean => {
    // Always allow access to the first 3 letters for a better user experience
    if (letter.order <= 3) return true;
    
    // For letters after the first 3, check if the previous letter's lesson is completed
    const previousLetter = alphabetLetters.find(l => l.order === letter.order - 1);
    const isAccessible = previousLetter ? completedLetters.includes(previousLetter.lessonId) : false;
    
            console.log('Letter ' + letter.letter + ' (' + letter.order + ') accessibility:', {
      previousLetter: previousLetter?.letter,
      previousLessonId: previousLetter?.lessonId,
      completedLessons: completedLetters,
      isAccessible
    });
    
    return isAccessible;
  };

  const getLetterProgress = (letter: AlphabetLetter): number => {
    return completedLetters.includes(letter.lessonId) ? 100 : 0;
  };

  const totalLetters = alphabetLetters.length;
  const completedCount = completedLetters.length;
  const progressPercentage = totalLetters > 0 ? (completedCount / totalLetters) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Persian Alphabet Grid
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Master all 32 letters of the Persian alphabet through interactive learning. We assume you already are done with alphabet learning section and for building foundations you can start learning alphabet here in order
          </p>
          
          {/* Progress Overview */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <FaGraduationCap className="text-2xl text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Your Progress</h3>
                  <p className="text-gray-600">{completedCount} of {totalLetters} letters mastered</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">{Math.round(progressPercentage)}%</div>
                <div className="text-sm text-gray-500">Complete</div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500"
                style={{ width: progressPercentage + '%' }}
              ></div>
            </div>
          </div>
        </div>

        {/* Alphabet Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-12">
          {alphabetLetters.map((letter) => {
            const progress = getLetterProgress(letter);
            const isCompleted = progress === 100;
            const isAccessible = isLetterAccessible(letter);
            const isLocked = !isAccessible;
            
            return (
              <div 
                key={letter.id}
                className={'alphabet-card bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-105 ' +
                  (isCompleted ? 'ring-2 ring-green-500' : '') +
                  ' ' + (isLocked ? 'opacity-70' : '')}
              >
                {/* Letter Display */}
                <div className="p-4 text-center border-b border-gray-200">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-white text-2xl font-bold">
                        {letter.letter}
                      </span>
                    </div>
                    
                    {/* Status Icons */}
                    {isCompleted && (
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <FaCheck className="text-white text-sm" />
                      </div>
                    )}
                    {isLocked && (
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-200 rounded-full flex items-center justify-center">
                        <FaLock className="text-orange-600 text-sm" />
                      </div>
                    )}
                  </div>
                  
                  <h3 className="text-base font-bold text-gray-900 mb-1">
                    {letter.letterNameEnglish}
                  </h3>
                  <p className="text-xs text-gray-600 mb-1">
                    {letter.letterNamePersian}
                  </p>
                  <p className="text-xs text-gray-500 mb-2">
                    Order: {letter.order}
                  </p>
                  
                  {/* Audio Button */}
                  {letter.audioUrl && (
                    <div className="flex flex-col items-center space-y-1">
                      <button
                        onClick={() => handleAudioPlay(letter)}
                        className={'w-10 h-10 rounded-full flex items-center justify-center transition-colors ' +
                          (audioErrors[letter.id] 
                            ? 'bg-red-100 hover:bg-red-200' 
                            : 'bg-blue-100 hover:bg-blue-200')}
                        disabled={playingAudio === letter.id}
                        title={audioErrors[letter.id] || 'Play pronunciation'}
                      >
                        {playingAudio === letter.id ? (
                          <FaVolumeUp className="text-blue-600 text-sm animate-pulse" />
                        ) : audioErrors[letter.id] ? (
                          <FaVolumeUp className="text-red-600 text-sm" />
                        ) : (
                          <FaPlay className="text-blue-600 text-sm" />
                        )}
                      </button>
                      {audioErrors[letter.id] && (
                        <div className="text-xs text-red-600 text-center max-w-20">
                          TTS fallback
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Letter Info */}
                <div className="p-3">
                  <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                    {letter.description}
                  </p>
                  
                  {/* Examples */}
                  <div className="mb-2">
                    <h4 className="text-xs font-semibold text-gray-700 mb-1">Examples:</h4>
                    <div className="flex flex-wrap gap-1">
                      {letter.examples.slice(0, 2).map((example, idx) => (
                        <div key={idx} className="flex items-center gap-1 bg-blue-100 text-blue-800 text-xs rounded px-1 py-0.5">
                          <span>{example}</span>
                          {letter.exampleAudioUrls[idx] && (
                            <button
                              onClick={() => handleExampleAudioPlay(letter, idx)}
                              className="w-4 h-4 rounded-full flex items-center justify-center bg-blue-200 hover:bg-blue-300 transition-colors"
                              disabled={playingAudio === (letter.id + '-example-' + idx)}
                              title={'Play pronunciation of ' + example}
                            >
                                                              {playingAudio === (letter.id + '-example-' + idx) ? (
                                <FaVolumeUp className="text-blue-600 text-xs animate-pulse" />
                              ) : (
                                <FaPlay className="text-blue-600 text-xs" />
                              )}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: progress + '%' }}
                    ></div>
                  </div>

                  {/* Action Button */}
                  {isAccessible ? (
                    <Link
                      to={'/pathways/alphabet-learning-pathway/lesson/' + letter.lessonId}
                      className="w-full py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition font-medium text-center block text-sm"
                    >
                      {isCompleted ? 'Review' : 'Learn'}
                    </Link>
                  ) : (
                    <button
                      disabled
                      className="w-full py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed font-medium text-sm"
                    >
                      Locked
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-4">Ready to Master the Persian Alphabet?</h2>
            <p className="text-white mb-6 max-w-2xl mx-auto">
              Start with the first letter and build your foundation in Persian reading and writing. 
              Each letter includes pronunciation practice, example words, and interactive activities.
            </p>
                          <Link
                to={'/pathways/alphabet-learning-pathway/lesson/' + (alphabetLetters[0]?.lessonId || '')}
                className="inline-flex items-center px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
              <FaPlay className="mr-2" />
              Start Learning Now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlphabetGrid; 